'use client';

import type { PropsWithChildren } from 'react';

import type { IconName } from '../icons';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { useState } from 'react';

import { Button } from '../../ui';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Icon } from '../icons';
import { Typography } from '../typography';
import { READ_STATUSES } from './common';
import {
  Notification,
  type NotificationItem,
  type NotificationStatus,
} from './notification';

const MAX_BADGE_COUNT = 99;
const MAX_BADGE_DISPLAY = '99+';

/**
 * Custom notification badge component.
 * Styles are based on Figma design specifications for notification count badges.
 */
const NotificationBadge = ({ count }: { count: number }) => {
  const displayCount = count > MAX_BADGE_COUNT ? MAX_BADGE_DISPLAY : count;
  return (
    <span className="flex min-h-5 min-w-5 items-center justify-center rounded-full border border-transparent bg-semantic-error-foreground p-1 text-xs font-semibold leading-none text-primary-foreground">
      {displayCount}
    </span>
  );
};

const NotificationCountBadge = ({ count }: { count: number }) => (
  <span className="flex items-center justify-center gap-1 rounded-full bg-semantic-info-background px-2.5 py-0.5 text-sm font-semibold text-semantic-info-foreground">
    {count}
  </span>
);

type NotificationLinkButtonProps = {
  icon: IconName;
  iconPosition?: 'left' | 'right';
  onClick: () => void;
};

/**
 * Custom link button for notification actions.
 * Uses design system specifications with custom hover behavior (no underline, opacity transition).
 */
const NotificationLinkButton = ({
  children,
  icon,
  iconPosition = 'left',
  onClick,
}: PropsWithChildren<NotificationLinkButtonProps>) => (
  <Button
    className="h-auto gap-2 p-0 no-underline hover:no-underline hover:opacity-80"
    variant="link"
    onClick={onClick}
  >
    {iconPosition === 'left' && <Icon name={icon} size="sm" />}
    {children}
    {iconPosition === 'right' && <Icon name={icon} size="sm" />}
  </Button>
);

const NotificationSection = ({
  children,
  className,
}: PropsWithChildren<{
  className?: string;
}>) => <div className={cn('h-16 px-4 py-3', className)}>{children}</div>;

export type NotificationEmptyStateProps = {
  title: string;
  description: string;
};

export const NotificationEmptyState = ({
  title,
  description,
}: NotificationEmptyStateProps) => (
  <div className="flex flex-col items-center justify-center gap-4 py-6 text-center">
    <div className="flex items-center justify-center rounded-lg bg-accent p-2.5">
      <Icon className="text-primary" name="mail-open" size="md" />
    </div>
    <div className="flex flex-col gap-1">
      <Typography as="h4" variant="title-6">
        {title}
      </Typography>
      <Typography className="text-muted-foreground">{description}</Typography>
    </div>
  </div>
);

const defaultLabels = {
  title: 'Notifications',
  markAllAsRead: 'Tout marquer comme lu',
  viewAll: 'Voir toutes les notifications',
  emptyStateTitle: 'Aucune notification',
  emptyStateDescription: 'Vous n’avez rien à consulter pour le moment.',
};

export type NotificationCenterProps = {
  notifications: NotificationItem[];
  onUpdateStatus: ({
    notificationId,
    status,
  }: {
    notificationId: string;
    status: NotificationStatus;
  }) => void;
  onMarkAllAsRead: () => void;
  onViewAll?: () => void;
  labels?: typeof defaultLabels;
  className?: string;
  'data-testid'?: string;
};

export const NotificationCenter = ({
  notifications,
  onUpdateStatus,
  onMarkAllAsRead,
  onViewAll,
  labels,
  className,
  'data-testid': dataTestId,
}: NotificationCenterProps) => {
  const [open, setOpen] = useState(false);

  const visibleNotifications = notifications.filter(
    (notification) => notification.status !== 'archived'
  );

  const actualUnreadCount = visibleNotifications.filter(
    (notification) => !READ_STATUSES.includes(notification.status)
  ).length;

  const hasUnread = actualUnreadCount > 0;

  const labelsActual = { ...defaultLabels, ...labels };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          isIcon
          className={cn('relative border border-border')}
          data-testid={dataTestId}
          variant="ghost"
        >
          {!!hasUnread && (
            <div className="absolute -right-3 -top-2">
              <NotificationBadge count={actualUnreadCount} />
            </div>
          )}
          <Icon name="bell" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className={cn('min-w-[500px] p-0', className)}
      >
        <NotificationSection className="flex items-center justify-between self-stretch border-b">
          <div className="flex items-center gap-2">
            <Typography as="h3" variant="title-6">
              {labelsActual.title}
            </Typography>
            <NotificationCountBadge count={actualUnreadCount} />
          </div>
          {!!hasUnread && !!onMarkAllAsRead && (
            <NotificationLinkButton
              icon="circle-check"
              onClick={onMarkAllAsRead}
            >
              {labelsActual.markAllAsRead}
            </NotificationLinkButton>
          )}
        </NotificationSection>

        <div
          className="flex max-h-[480px] flex-col overflow-y-auto"
          role="list"
        >
          {visibleNotifications.length === 0 ? (
            <NotificationEmptyState
              description={labelsActual.emptyStateDescription}
              title={labelsActual.emptyStateTitle}
            />
          ) : (
            visibleNotifications.map((notification) => {
              const notificationId = notification.id;
              return (
                <Notification
                  key={notificationId}
                  {...notification}
                  onUpdateStatus={(status) =>
                    onUpdateStatus({ notificationId, status })
                  }
                />
              );
            })
          )}
        </div>

        <NotificationSection className="border-t text-center">
          <NotificationLinkButton
            icon="chevron-down"
            iconPosition="right"
            onClick={() => {
              setOpen(false);
              onViewAll?.();
            }}
          >
            {labelsActual.viewAll}
          </NotificationLinkButton>
        </NotificationSection>
      </PopoverContent>
    </Popover>
  );
};
